import { useHttpClient, useToast } from '@airship-platform/airship-patterns';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ProposalTemplateTag } from './proposalTemplateTags.models';
import {
  initState,
  proposalTemplateTagsReducer,
  ProposalTemplateTagsState,
} from './proposalTemplateTags.reducer';

type CreatePayload = Pick<
  ProposalTemplateTag,
  'name' | 'groupingName' | 'sortOrder'
>;

interface ProposalTemplateTagsContextProps {
  proposalTemplateTags: ProposalTemplateTag[];
  isLoading: boolean;
  isLoaded: boolean;
  isDeletingTagIds: string[];
  error: string | null;
  fetchProposalTemplateTags: () => Promise<void>;
  fetchProposalTemplateTag: (proposalTemplateTagId: string) => Promise<void>;
  createProposalTemplateTag: (
    data: CreatePayload
  ) => Promise<Pick<ProposalTemplateTag, 'proposalTemplateTagId'>>;
  updateProposalTemplateTag: (
    proposalTemplateTagId: string,
    data: Partial<CreatePayload>
  ) => Promise<void>;
  deleteProposalTemplateTag: (proposalTemplateTagId: string) => Promise<void>;
}

const ProposalTemplateTagsContext = createContext<
  ProposalTemplateTagsContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<ProposalTemplateTagsState>;
}>;

export const ProposalTemplateTagsProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(proposalTemplateTagsReducer, {
    ...initState,
    ...initialState,
  });
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const proposalTemplateTags = useMemo(
    () =>
      state.ids
        .flatMap((id) => state.entities[id] || [])
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids]
  );

  const fetchProposalTemplateTags: ProposalTemplateTagsContextProps['fetchProposalTemplateTags'] =
    useCallback(async () => {
      dispatch({ type: 'FETCH_PROPOSAL_TEMPLATE_TAGS' });
      try {
        const proposalTemplateTags = await httpClient.get<
          ProposalTemplateTag[]
        >(`/proposal-templates/tags`);
        dispatch({
          type: 'FETCH_PROPOSAL_TEMPLATE_TAGS_SUCCESS',
          payload: { proposalTemplateTags },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_PROPOSAL_TEMPLATE_TAGS_FAILURE',
          payload: { error: `${error}` },
        });
        throw error;
      }
    }, [httpClient]);

  const fetchProposalTemplateTag: ProposalTemplateTagsContextProps['fetchProposalTemplateTag'] =
    useCallback(
      async (proposalTemplateTagId) => {
        dispatch({
          type: 'FETCH_PROPOSAL_TEMPLATE_TAG',
          payload: { proposalTemplateTagId },
        });
        try {
          const proposalTemplateTag = await httpClient.get<ProposalTemplateTag>(
            `/proposal-templates/tags/${proposalTemplateTagId}`
          );
          dispatch({
            type: 'FETCH_PROPOSAL_TEMPLATE_TAG_SUCCESS',
            payload: { proposalTemplateTag },
          });
        } catch (error) {
          dispatch({
            type: 'FETCH_PROPOSAL_TEMPLATE_TAG_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const createProposalTemplateTag: ProposalTemplateTagsContextProps['createProposalTemplateTag'] =
    useCallback(
      async (data) => {
        dispatch({ type: 'CREATE_PROPOSAL_TEMPLATE_TAG' });
        try {
          const proposalTemplateTag =
            await httpClient.post<ProposalTemplateTag>(
              `/proposal-templates/tags`,
              data
            );

          dispatch({
            type: 'CREATE_PROPOSAL_TEMPLATE_TAG_SUCCESS',
            payload: { proposalTemplateTag },
          });

          return proposalTemplateTag;
        } catch (error) {
          dispatch({
            type: 'CREATE_PROPOSAL_TEMPLATE_TAG_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const updateProposalTemplateTag: ProposalTemplateTagsContextProps['updateProposalTemplateTag'] =
    useCallback(
      async (proposalTemplateTagId, data) => {
        dispatch({ type: 'UPDATE_PROPOSAL_TEMPLATE_TAG' });
        try {
          const proposalTemplateTag = await httpClient.put<ProposalTemplateTag>(
            `/proposal-templates/tags/${proposalTemplateTagId}`,
            data
          );

          dispatch({
            type: 'UPDATE_PROPOSAL_TEMPLATE_TAG_SUCCESS',
            payload: { proposalTemplateTag },
          });
        } catch (error) {
          dispatch({
            type: 'UPDATE_PROPOSAL_TEMPLATE_TAG_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const deleteProposalTemplateTag: ProposalTemplateTagsContextProps['deleteProposalTemplateTag'] =
    useCallback(
      async (proposalTemplateTagId) => {
        dispatch({
          type: 'DELETE_PROPOSAL_TEMPLATE_TAG',
          payload: { proposalTemplateTagId },
        });
        try {
          await httpClient.delete(
            `/proposal-templates/tags/${proposalTemplateTagId}`
          );
          dispatch({
            type: 'DELETE_PROPOSAL_TEMPLATE_TAG_SUCCESS',
            payload: { proposalTemplateTagId },
          });
        } catch (error) {
          dispatch({
            type: 'DELETE_PROPOSAL_TEMPLATE_TAG_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  return (
    <ProposalTemplateTagsContext.Provider
      value={{
        proposalTemplateTags,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isDeletingTagIds: state.isDeletingTagIds,
        error: state.error,
        fetchProposalTemplateTag,
        fetchProposalTemplateTags,
        createProposalTemplateTag,
        updateProposalTemplateTag,
        deleteProposalTemplateTag,
      }}
    >
      {children}
    </ProposalTemplateTagsContext.Provider>
  );
};

export const useProposalTemplateTags = (): ProposalTemplateTagsContextProps => {
  const context = useContext(ProposalTemplateTagsContext);
  if (context === undefined) {
    throw new Error(
      'useProposalTemplateTags must be used within an ProposalTemplateTagsProvider'
    );
  }
  return context;
};
